import { Component } from '@theme/component';

/**
 * WhatsApp chat button, fixed bottom right (or left, a setting) on every page but the cart.
 *
 * The round button is a plain wa.me link in the markup, so the chat opens even if this script
 * never runs. Once it runs, the button becomes a toggle for the small panel, whose contact card
 * holds the same link. Closes on Escape, on a tap outside and on the button itself (it shows a
 * cross while open).
 *
 * Lifts above the product page's sticky add-to-cart bar while that bar shows, by publishing its
 * height as `--oudh-wa-lift`.
 *
 * @extends {Component<{ toggle: HTMLAnchorElement, panel: HTMLElement, link: HTMLAnchorElement }>}
 */
class OudhWhatsapp extends Component {
  requiredRefs = ['toggle', 'panel', 'link'];

  /** @type {MutationObserver | undefined} */
  #barObserver;

  /** @type {ResizeObserver | undefined} */
  #barResize;

  connectedCallback() {
    super.connectedCallback();
    const { toggle, panel } = this.refs;
    toggle.setAttribute('role', 'button');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', panel.id);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onOutside, true);
    this.#watchStickyBar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onOutside, true);
    this.#barObserver?.disconnect();
    this.#barResize?.disconnect();
  }

  /** @param {Event} event */
  toggle(event) {
    event.preventDefault();
    if (this.refs.panel.hidden) this.#open();
    else this.#close(true);
  }

  #open() {
    const { panel, toggle, link } = this.refs;
    panel.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    link.focus({ preventScroll: true });
  }

  /** @param {boolean} returnFocus */
  #close(returnFocus) {
    const { panel, toggle } = this.refs;
    if (panel.hidden) return;
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    if (returnFocus) toggle.focus({ preventScroll: true });
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') this.#close(true);
  };

  /** @param {MouseEvent} event */
  #onOutside = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#close(false);
  };

  /** Product page only: keep clear of the sticky add-to-cart bar while it shows. */
  #watchStickyBar() {
    const bar = document.querySelector('oudh-sticky-cart');
    if (!(bar instanceof HTMLElement)) return;

    const update = () => {
      const lift = bar.hasAttribute('data-visible') ? bar.offsetHeight : 0;
      this.style.setProperty('--oudh-wa-lift', `${lift}px`);
    };
    this.#barObserver = new MutationObserver(update);
    this.#barObserver.observe(bar, { attributes: true, attributeFilter: ['data-visible'] });
    this.#barResize = new ResizeObserver(update);
    this.#barResize.observe(bar);
    update();
  }
}

if (!customElements.get('oudh-whatsapp')) {
  customElements.define('oudh-whatsapp', OudhWhatsapp);
}
